import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { fetchMovies } from "../redux/slices/moviesSlice";
import { Play, UserCircle } from "lucide-react";
import { fetchUsers } from "../redux/slices/usersSlice";
import axios from "axios";
import { getDeviceId, getDeviceName } from "../utils/deviceId";
import { trackPlaybackEvent } from "../services/playbackService";

/* =======================
   Botón de Cast/AirPlay
   ======================= */
const CastButton = ({ movie }) => {
  const [isCastInitialized, setIsCastInitialized] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [castingMethod, setCastingMethod] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("");

  useEffect(() => {
    if (isCastInitialized) return;

    if (window.cast && window.cast.framework) {
      try {
        initializeCastApi();
        setIsCastInitialized(true);
      } catch (error) {
        console.error("Error al inicializar directamente:", error);
      }
      return;
    }

    if (!window["__onGCastApiAvailable"]) {
      window["__onGCastApiAvailable"] = function (isAvailable) {
        if (isAvailable && !isCastInitialized) {
          try {
            initializeCastApi();
            setIsCastInitialized(true);
          } catch (error) {
            console.error("Error al inicializar Google Cast en callback:", error);
          }
        }
      };
    }

    loadCastScript();
  }, [isCastInitialized]);

  const [castScriptLoaded, setCastScriptLoaded] = useState(false);

  const loadCastScript = () => {
    if (castScriptLoaded || document.getElementById("cast-api-script")) return;
    setCastScriptLoaded(true);
    const script = document.createElement("script");
    script.id = "cast-api-script";
    script.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    document.body.appendChild(script);
  };

  const initializeCastApi = () => {
    if (isCastInitialized) return;
    if (!window.chrome || !window.chrome.cast) {
      console.error("La API de Chrome Cast no está disponible");
      return;
    }
    try {
      if (
        window.cast &&
        window.cast.framework &&
        window.cast.framework.CastContext &&
        window.cast.framework.CastContext.getInstance
      ) {
        return;
      }
      const applicationId = window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
      const sessionRequest = new window.chrome.cast.SessionRequest(applicationId);
      const apiConfig = new window.chrome.cast.ApiConfig(
        sessionRequest,
        (session) => {
          setConnectionStatus(`Conectado a ${session.getCastDevice().friendlyName}`);
          if (movie && movie.movieUrl) loadMediaToCast(session, movie);
        },
        (availability) => {},
        window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
      );
      window.chrome.cast.initialize(apiConfig, () => {}, (e) => console.error(e));
    } catch (error) {
      console.error("Error al inicializar la API de Cast:", error);
    }
  };

  const loadMediaToCast = (session, movieData) => {
    if (!session) return;
    try {
      const mediaUrl = movieData.movieUrl || "";
      const movieName = movieData.name || "Película";
      const posterUrl = movieData.posterUrl || "";

      const mediaInfo = new chrome.cast.media.MediaInfo(mediaUrl, "video/mp4");
      const movieMetadata = new chrome.cast.media.MovieMediaMetadata();
      movieMetadata.title = movieName;
      if (posterUrl) movieMetadata.images = [{ url: posterUrl }];

      mediaInfo.metadata = movieMetadata;
      mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;

      const request = new chrome.cast.media.LoadRequest(mediaInfo);
      session
        .loadMedia(request)
        .then(() => setConnectionStatus(`Reproduciendo "${movieName}" en TV`))
        .catch((error) => {
          console.error("Error al cargar contenido en Chromecast:", error);
          setConnectionStatus("Error al cargar el contenido en TV");
        });
    } catch (error) {
      console.error("Error en el proceso de carga de medios:", error);
    }
  };

  const toggleHelp = () => setShowHelp(!showHelp);

  const handleChromecastClick = () => {
    setCastingMethod("chromecast");

    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isBrave = navigator.brave !== undefined || (!isChrome && /Chrome/.test(navigator.userAgent));
    const isEdge = /Edg/.test(navigator.userAgent);

    if (!window.cast || !window.cast.framework) {
      if (isBrave) setConnectionStatus("Brave puede bloquear Google Cast. Intenta con Chrome o permite la API en Escudos.");
      else if (isEdge) setConnectionStatus("Edge puede tener limitaciones con Cast. Recomendamos Chrome.");
      else if (!isChrome) setConnectionStatus("Google Cast es plenamente compatible con Google Chrome.");
      else setConnectionStatus("Google Cast no está disponible en este navegador");
      setIsConnecting(true);
      setTimeout(() => setIsConnecting(false), 5000);
      return;
    }

    try {
      setIsConnecting(true);
      setConnectionStatus("Preparando conexión a Chromecast...");

      const CastContext = window.cast.framework.CastContext;
      const context = CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });

      const handleSessionStart = () => {
        const session = context.getCurrentSession();
        if (session && movie && movie.movieUrl) {
          const mediaInfo = new window.chrome.cast.media.MediaInfo(movie.movieUrl, "video/mp4");
          if (movie.name) {
            const metadata = new window.chrome.cast.media.MovieMediaMetadata();
            metadata.title = movie.name;
            if (movie.posterUrl) metadata.images = [{ url: movie.posterUrl }];
            mediaInfo.metadata = metadata;
          }
          mediaInfo.streamType = window.chrome.cast.media.StreamType.BUFFERED;
          const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
          setTimeout(() => {
            session
              .loadMedia(request)
              .then(() => setConnectionStatus(`Reproduciendo "${movie.name || "película"}" en TV`))
              .catch((err) => {
                console.error("Error al cargar contenido:", err);
                setConnectionStatus("Error al cargar contenido en TV");
              });
          }, 1000);
        }
      };

      const SessionState = window.cast.framework.SessionState;
      context.addEventListener(
        window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event) => {
          switch (event.sessionState) {
            case SessionState.SESSION_STARTED:
              handleSessionStart();
              break;
            case SessionState.SESSION_ENDED:
              setConnectionStatus("Desconectado");
              setTimeout(() => setIsConnecting(false), 1000);
              break;
            case SessionState.SESSION_RESUMED:
              handleSessionStart();
              break;
          }
        }
      );

      setConnectionStatus("Selecciona un dispositivo Chromecast...");
      context
        .requestSession()
        .then(() => {})
        .catch((error) => {
          if (error.code === "cancel") setConnectionStatus("Selección cancelada");
          else setConnectionStatus("No se pudo conectar al dispositivo");
          setTimeout(() => setIsConnecting(false), 2000);
        });
    } catch (error) {
      console.error("Error general en Chromecast:", error);
      setConnectionStatus("Error al iniciar Chromecast");
      setTimeout(() => setIsConnecting(false), 2000);
    }
  };

  const handleAirPlayClick = () => {
    setCastingMethod("airplay");
    try {
      setIsConnecting(true);
      setConnectionStatus("Preparando AirPlay...");
      const videoElement = document.querySelector("video");
      if (videoElement && videoElement.webkitShowPlaybackTargetPicker) {
        videoElement.webkitShowPlaybackTargetPicker();
        setConnectionStatus("Selecciona tu dispositivo Apple TV");
      } else {
        setConnectionStatus("Tu navegador no es compatible con AirPlay");
        setTimeout(() => setIsConnecting(false), 2000);
      }
    } catch (error) {
      console.error("Error al iniciar AirPlay:", error);
      setConnectionStatus("Error al iniciar AirPlay");
      setTimeout(() => setIsConnecting(false), 2000);
    }
  };

  return (
    <div className="cast-button-wrapper relative">
      <div className="flex space-x-2">
        {isConnecting && (
          <div className="connection-status flex items-center mr-3">
            <div className={`pulse-dot w-2 h-2 rounded-full ${connectionStatus.includes("Error") ? "bg-red-500" : "bg-blue-500"} mr-2`} />
            <span className="text-xs text-white">{connectionStatus}</span>
          </div>
        )}
        <button
          onClick={handleChromecastClick}
          className={`${castingMethod === "chromecast" && isConnecting ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"} text-white px-3 py-1 rounded transition-colors`}
          title="Transmitir a Chromecast"
          disabled={isConnecting && castingMethod !== "chromecast"}
        >
          Chromecast
        </button>
        <button
          onClick={handleAirPlayClick}
          className={`${castingMethod === "airplay" && isConnecting ? "bg-blue-600" : "bg-gray-700 hover:bg-gray-600"} text-white px-3 py-1 rounded transition-colors`}
          title="Transmitir con AirPlay"
          disabled={isConnecting && castingMethod !== "airplay"}
        >
          AirPlay
        </button>
        <button onClick={toggleHelp} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded" title="Ayuda">
          ?
        </button>
      </div>

      {showHelp && (
        <div className="help-panel absolute bg-black text-white p-4 rounded-md shadow-lg z-10 mt-2 max-w-md">
          <button className="absolute top-2 right-2 text-white hover:text-gray-300" onClick={() => setShowHelp(false)}>
            X
          </button>
          <h3 className="text-lg font-bold mb-2">Cómo transmitir a tu TV</h3>
          <div className="mb-3">
            <h4 className="font-bold">Chromecast:</h4>
            <ol className="list-decimal pl-5 text-sm">
              <li>Conecta el Chromecast a la misma WiFi.</li>
              <li>Usa Google Chrome.</li>
              <li>Haz clic en “Chromecast” y elige el dispositivo.</li>
            </ol>
          </div>
          <div>
            <h4 className="font-bold">AirPlay:</h4>
            <ol className="list-decimal pl-5 text-sm">
              <li>Apple TV / TV con AirPlay en la misma red.</li>
              <li>Usa Safari en macOS o iOS.</li>
              <li>Haz clic en “AirPlay” y selecciona tu dispositivo.</li>
            </ol>
          </div>
          <p className="text-xs mt-3 text-gray-400">Si hay problemas, revisa que tu red no bloquee la detección.</p>
        </div>
      )}
    </div>
  );
};

/* =======================
   MoviePlayer
   ======================= */
const MoviePlayer = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { movies, loading: moviesLoading } = useSelector((state) => state.movies);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const [movie, setMovie] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // Tracking
  const lastTrackingRef = useRef(Date.now());
  const movieDurationRef = useRef(0);
  const currentPositionRef = useRef(0);
  const trackingIntervalRef = useRef(null);
  const [trackingStatus, setTrackingStatus] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("token"));

  const TRACKING_INTERVAL = 30000;
  const TRACKING_THRESHOLD = 5;

  useEffect(() => {
    dispatch(fetchMovies());
  }, [dispatch]);

  useEffect(() => {
    if (user?.role === "admin") {
      dispatch(fetchUsers());
    }
  }, [dispatch, user?.role]);

  useEffect(() => {
    const checkSubscription = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setIsAuthenticated(false);
        setCheckingSubscription(false);
        return;
      }

      if (user?.role === "admin") {
        setHasActiveSubscription(true);
        setCheckingSubscription(false);
        return;
      }

      try {
        if (!user?.id) {
          setCheckingSubscription(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
        const response = await axios.get(`${apiUrl}/api/v1/userSubscriptions/${user.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setSubscriptions(response.data);

        if (Array.isArray(response.data) && response.data.length > 0) {
          const now = new Date();
          const active = response.data.find(
            (sub) => sub.status === "active" && new Date(sub.startDate) <= now && new Date(sub.endDate) > now
          );
          setHasActiveSubscription(Boolean(active));
        } else {
          setHasActiveSubscription(false);
        }
      } catch (error) {
        console.error("Error al obtener información de suscripciones:", error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user?.id, id, navigate]);

  useEffect(() => {
    if (!user) return;

    const fetchMovieForPlayback = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const deviceId = getDeviceId();
        const deviceName = getDeviceName();

        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
        const response = await axios.get(`${apiUrl}/api/v1/movies/play/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "user-device-id": deviceId,
            "user-device-name": deviceName,
          },
        });

        const movieData = response.data.movie || response.data;
        setMovie(movieData);
        setHasActiveSubscription(true);
      } catch (error) {
        console.error("Error al obtener la película:", error);

        if (axios.isAxiosError(error)) {
          if (error.response && (error.response.status === 403 || error.response.status === 400)) {
            const errorMessage = error.response.data?.error || "No se puede reproducir en este dispositivo";
            if (error.response.status !== 403 || user?.role === "admin") {
              alert(errorMessage);
            }
            if (error.response.status === 403 && user?.role !== "admin") {
              setHasActiveSubscription(false);
            }
          }
        }
      }
    };

    fetchMovieForPlayback();
  }, [id, user]);

  useEffect(() => {
    let player;
    const videoElement = videoRef.current;

    if (movie?.movieUrl && videoElement) {
      // endurecer controles
      videoElement.setAttribute("controlsList", "nodownload nofullscreen");
      videoElement.setAttribute("disablePictureInPicture", "true");
      videoElement.setAttribute("oncontextmenu", "return false;");

      player = videojs(videoElement, {
        controls: true,
        autoplay: false,
        preload: "auto",
        controlBar: {
          pictureInPictureToggle: false,
          downloadButton: false,
          fullscreenToggle: true,
        },
        userActions: {
          hotkeys: false,
        },
        sources: [
          {
            src: movie.movieUrl,
            type: "video/mp4",
          },
        ],
      });

      playerRef.current = player;

      // No agregamos pistas por JS: usamos SOLO el <track> HTML (botón CC nativo)

      player.on("loadedmetadata", () => {
        const durationInSeconds = Math.round(player.duration());
        movieDurationRef.current = durationInSeconds;
        console.log(`Video metadata loaded. Duration: ${durationInSeconds}s`);
      });

      player.on("play", () => {
        const currentTime = Math.round(player.currentTime());
        currentPositionRef.current = currentTime;

        const eventType = currentTime === 0 ? "start" : "resume";
        trackPlaybackEvent(id, eventType, currentTime, movieDurationRef.current);
        startTrackingInterval();
      });

      player.on("pause", () => {
        const currentTime = Math.round(player.currentTime());
        currentPositionRef.current = currentTime;
        trackPlaybackEvent(id, "pause", currentTime, movieDurationRef.current);
        clearTrackingInterval();
      });

      player.on("seeking", () => {
        const currentTime = Math.round(player.currentTime());
        if (Math.abs(currentTime - currentPositionRef.current) > TRACKING_THRESHOLD) {
          trackPlaybackEvent(id, "seek", currentTime, movieDurationRef.current);
          currentPositionRef.current = currentTime;
        }
      });

      player.on("ended", () => {
        const currentTime = Math.round(player.currentTime());
        currentPositionRef.current = currentTime;
        trackPlaybackEvent(id, "end", currentTime, movieDurationRef.current);
        clearTrackingInterval();
      });

      if (navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function") {
        const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
        navigator.mediaDevices.getDisplayMedia = function (constraints) {
          if (videoElement && !videoElement.paused) {
            player.pause();
            alert("La grabación de pantalla no está permitida durante la reproducción");
          }
          return originalGetDisplayMedia.call(this, constraints);
        };
      }
    }

    const startTrackingInterval = () => {
      if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);

      trackingIntervalRef.current = setInterval(() => {
        if (playerRef.current && !playerRef.current.paused()) {
          const currentTime = Math.round(playerRef.current.currentTime());
          if (Math.abs(currentTime - currentPositionRef.current) > TRACKING_THRESHOLD) {
            trackPlaybackEvent(id, "update", currentTime, movieDurationRef.current);
            currentPositionRef.current = currentTime;
            lastTrackingRef.current = Date.now();
            setTrackingStatus(`Tracking: ${currentTime}s`);
          }
        }
      }, TRACKING_INTERVAL);
    };

    const clearTrackingInterval = () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };

    return () => {
      if (player && player.dispose) player.dispose();
      clearTrackingInterval();

      if (
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getDisplayMedia === "function" &&
        navigator.mediaDevices.getDisplayMedia.toString().includes("player.pause")
      ) {
        delete navigator.mediaDevices.getDisplayMedia;
      }
    };
  }, [movie, id]);

  // Estilos pequeñitos para Cast
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      google-cast-launcher { --connected-color: #1db954; --disconnected-color: white; }
      .cast-controls { display: flex; align-items: center; margin-top: 8px; }
      .cast-button-wrapper { display: flex; align-items: center; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  if (moviesLoading || checkingSubscription)
    return (
      <div className="bg-black w-full h-screen flex items-center justify-center">
        <h1 className="text-white text-2xl">Cargando película...</h1>
      </div>
    );

  const renderAuthenticationMessage = () => (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-black to-gray-900 p-4">
      <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">Acceso Exclusivo</h2>
        <p className="text-gray-300 mb-6">Para disfrutar de este contenido necesitas iniciar sesión o registrarte.</p>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    localStorage.setItem("intendedMoviePath", `/movies/movie/${id}`);
    return renderAuthenticationMessage();
  }

  const renderNoSubscriptionMessage = () => (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-black to-gray-900 p-4">
      <div className="bg-black/60 p-6 rounded-lg max-w-md text-center">
        <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="w-8 h-8 text-black" />
        </div>
        <h2 className="text-2xl font-bold mb-2 text-white">Suscripción Requerida</h2>
        <p className="text-gray-300 mb-4">Necesitas una suscripción vigente para reproducir películas.</p>
        {subscriptions && subscriptions.length > 0 ? (
          <div className="mb-4 text-left text-sm bg-gray-800/50 p-3 rounded">
            <p className="font-semibold text-white mb-1">Estado de tus suscripciones:</p>
            {subscriptions.map((sub) => (
              <div key={sub.id} className="mb-2 text-gray-300">
                <p>• Estado: <span className={sub.status === "active" ? "text-green-400" : "text-red-400"}>{sub.status}</span></p>
                <p>• Válida: {new Date(sub.startDate).toLocaleDateString()} a {new Date(sub.endDate).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 mb-4">No tienes ninguna suscripción registrada.</p>
        )}
        <Link to="/subscriptionplan" className="block w-full bg-yellow-500 hover:bg-yellow-400 text-black font-medium py-2 px-4 rounded transition-colors">
          Ver planes disponibles
        </Link>
      </div>
    </div>
  );

  if (!hasActiveSubscription) return renderNoSubscriptionMessage();

  if (!movie || !movie.movieUrl) {
    return (
      <div className="relative w-full min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <div className="text-xl mb-4">No video available</div>
        <button onClick={() => navigate(-1)} className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg hover:bg-opacity-70 mt-4">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen bg-black" ref={containerRef}>
      <div className="absolute inset-0 z-10 bg-cover bg-center"></div>

      <div className="flex flex-col items-center justify-center w-full h-full p-4 box-border z-10 relative">
        <p className="text-lg text-gray-300 mb-8">{movie.description}</p>

        {/* Contenedor del video */}
        <div className="relative w-full">
          <video
            ref={videoRef}
            className="video-js vjs-default-skin w-full h-[80vh] object-cover"
            controls
            controlsList="nodownload nofullscreen"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Fuente del video */}
            <source src={movie.movieUrl} type="video/mp4" />

            {/* ÚNICA PISTA DE SUBTÍTULOS (CC) desde backend como VTT */}
            {movie?.id && (
              <track
                key={movie.id}
                label="Español"
                kind="subtitles"
                srcLang="es"
                src={`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/v1/subtitles/${movie.id}.vtt`}
              />
            )}

            Your browser does not support the video tag.
          </video>

          {/* Controles inferiores */}
          <div className="w-full bg-gray-900 p-3 flex items-center justify-between">
            <div className="cast-controls flex items-center">
              {/* 👇 Ya no hay botón de Subtítulos personalizado; solo CC nativo */}
              <CastButton movie={movie} />
            </div>

            {trackingStatus && <div className="text-white text-sm">{trackingStatus}</div>}

            <button onClick={() => navigate("/home")} className="text-white bg-black bg-opacity-50 px-4 py-2 rounded-lg hover:bg-opacity-70">
             Volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoviePlayer;
